// The jump bar's completion: pluggable sources, each listing rows for what
// is typed, merged in the order the sources are given. It reaches for no
// window, network or storage itself: a source does (and is given what it
// reads by whoever makes it), and a remote source's debounce runs on the
// schedule the completer is given.
//
// The order of the sources is the order of the list. The app's
// (components/JumpBar.svelte) is the navigation spec's: what the text
// names by itself; in a repository, its branches and its open pull
// requests; anywhere, repositories (the repository index's, plugged in
// by App), the pull requests waiting on the viewer, recents and pins. An
// empty text is what a source answers to it: the pull requests waiting on
// the viewer, then recents, then pins.
import type { Repo, Row } from './rows';

/** What a source is asked: the text as typed, the repository the page is about, and a signal that aborts once the text changes. */
export interface JumpQuery {
  text: string;
  repo: Repo | null;
  signal: AbortSignal;
}

export interface Source {
  /** Unique among the sources. */
  readonly id: string;
  /** The heading its rows are listed under. */
  readonly title: string;
  /** Its rows for `q`, best first: at once, or (a remote source) when they arrive. A source that fails lists nothing, and is named as failed; one that throws a `PartialAnswer` lists its rows, and is named as failed. */
  rows(q: JumpQuery): readonly Row[] | Promise<readonly Row[]>;
  /** Whether it has anything for `q` (every query by default): one that has not is cleared at once, and not asked. */
  wants?(q: JumpQuery): boolean;
  /** Ask only once the text has rested this long (ms): a remote source's. */
  readonly debounceMs?: number;
  /** The most rows it lists (8 by default). */
  readonly limit?: number;
  /** The bar opened again: forget what was read (a source keeps what it read while the bar stays open). */
  reset?(): void;
}

/**
 * What a source throws (or rejects with) when it could read only part of
 * what it lists: its rows are listed, and the source is named as failed
 * all the same, so what it could not read is said.
 */
export class PartialAnswer extends Error {
  /** What could not be read, as the bar names it ("Could not read: …"); the source's title when not said. */
  readonly what: string | undefined;
  constructor(
    readonly rows: readonly Row[],
    o: { what?: string; message?: string } = {},
  ) {
    super(o.message ?? 'Read in part');
    this.what = o.what;
  }
}

export interface Section {
  id: string;
  title: string;
  rows: readonly Row[];
}

const LIMIT = 8;

/**
 * The sections of the sources' rows, in the sources' order, empty ones
 * left out. A row is listed once, where it first shows (by its id, or one
 * it stands for, `aliases`). A row read from the text alone
 * (`provisional`) gives its place to a later one that stands for it, so
 * what a source knows of a branch (its pull request, the default branch)
 * shows where the text put it; once the sources have answered
 * (`settled`) and none stands for it, it is listed last, as typed, so
 * Enter takes what the repository has before a name it may not.
 */
export function merge(results: readonly { source: Source; rows: readonly Row[] }[], o: { settled: boolean } = { settled: true }): Section[] {
  const out: { id: string; title: string; rows: Row[] }[] = [];
  const seen = new Map<string, { rows: Row[]; at: number }>();
  for (const { source, rows } of results) {
    const mine: Row[] = [];
    const limit = source.limit ?? LIMIT;
    for (const row of rows) {
      const names = [row.id, ...(row.aliases ?? [])];
      const hit = names.map((n) => seen.get(n)).find((h) => h !== undefined);
      if (hit) {
        if (hit.rows[hit.at].provisional) {
          hit.rows[hit.at] = row;
          for (const n of names) seen.set(n, hit);
        }
        continue;
      }
      if (mine.length >= limit) continue;
      const at = { rows: mine, at: mine.length };
      mine.push(row);
      for (const n of names) seen.set(n, at);
    }
    if (mine.length) out.push({ id: source.id, title: source.title, rows: mine });
  }
  if (!o.settled) return out;
  const typed: Section[] = [];
  const kept = out.flatMap((s) => {
    const guesses = s.rows.filter((r) => r.provisional);
    if (guesses.length) typed.push({ id: `${s.id}:as-typed`, title: 'As typed', rows: guesses });
    const rows = s.rows.filter((r) => !r.provisional);
    return rows.length ? [{ ...s, rows }] : [];
  });
  return [...kept, ...typed];
}

/** Run `run` after `ms`; the function returned cancels it. */
export type Schedule = (run: () => void, ms: number) => () => void;

const timeout: Schedule = (run, ms) => {
  const t = setTimeout(run, ms);
  return () => clearTimeout(t);
};

const isPromise = <T>(v: T | Promise<T>): v is Promise<T> => typeof (v as Promise<T> | null)?.then === 'function';

export interface CompleterOptions {
  sources: readonly Source[];
  /** Told whenever the sections, or what is being asked, change. */
  onChange: () => void;
  /** The debounce's clock (`setTimeout` by default). */
  schedule?: Schedule;
}

/**
 * The sources asked for what is typed. A source that answers at once is
 * listed at once; a remote one keeps its rows for the text before until
 * its answer to this one arrives (so the list does not empty under the
 * typing), and an answer to any text but the last is dropped. Each new
 * text aborts what was asked for the one before.
 */
export class Completer {
  readonly #sources: readonly Source[];
  readonly #onChange: () => void;
  readonly #schedule: Schedule;
  #rows = new Map<string, readonly Row[]>();
  #asking = new Set<string>();
  #failed = new Set<string>();
  /** What a failed source said it could not read, by id (`PartialAnswer.what`). */
  #failedWhat = new Map<string, string>();
  #generation = 0;
  #abort: AbortController | null = null;
  #timers: (() => void)[] = [];
  /** The debounced asks waiting on their timers, to run now on `flush`. */
  #due: (() => void)[] = [];

  constructor(o: CompleterOptions) {
    this.#sources = o.sources;
    this.#onChange = o.onChange;
    this.#schedule = o.schedule ?? timeout;
  }

  /** The sources' rows for the text last asked (or, a remote source's, the one before), merged; what the text alone names holds its place while a source is still asked. */
  get sections(): Section[] {
    return merge(
      this.#sources.map((source) => ({ source, rows: this.#rows.get(source.id) ?? [] })),
      { settled: this.#asking.size === 0 },
    );
  }

  /** The titles of the sources still being asked. */
  get asking(): string[] {
    return this.#sources.filter((s) => this.#asking.has(s.id)).map((s) => s.title);
  }

  /** The titles of the sources whose last answer failed. */
  get failed(): string[] {
    return this.#sources.filter((s) => this.#failed.has(s.id)).map((s) => this.#failedWhat.get(s.id) ?? s.title);
  }

  #stop() {
    this.#abort?.abort();
    this.#abort = null;
    this.#timers.splice(0).forEach((cancel) => cancel());
    this.#due = [];
  }

  /** Whether no source is being asked: every source has answered the text last asked. */
  get settled(): boolean {
    return this.#asking.size === 0;
  }

  /** Ask the debounced sources now, without waiting for the typing to rest (Enter was pressed). */
  flush(): void {
    this.#timers.splice(0).forEach((cancel) => cancel());
    this.#due.splice(0).forEach((run) => run());
  }

  /** Forget everything: the bar opened again. */
  reset(): void {
    this.#stop();
    this.#generation++;
    this.#rows.clear();
    this.#asking.clear();
    this.#failed.clear();
    this.#failedWhat.clear();
    for (const s of this.#sources) s.reset?.();
    this.#onChange();
  }

  ask(text: string, repo: Repo | null): void {
    this.#stop();
    const generation = ++this.#generation;
    const abort = new AbortController();
    this.#abort = abort;
    const q: JumpQuery = { text, repo, signal: abort.signal };
    // What answers at once is told once, with the rest of the ask.
    let asking = true;
    const settle = (s: Source, rows: readonly Row[] | null, partial?: PartialAnswer) => {
      if (generation !== this.#generation) return;
      this.#asking.delete(s.id);
      this.#failedWhat.delete(s.id);
      if (rows) {
        this.#rows.set(s.id, rows);
        if (partial) {
          this.#failed.add(s.id);
          if (partial.what) this.#failedWhat.set(s.id, partial.what);
        } else this.#failed.delete(s.id);
      } else {
        // What it listed was for another text: nothing of it is an answer to this one.
        this.#rows.delete(s.id);
        this.#failed.add(s.id);
      }
      if (!asking) this.#onChange();
    };
    for (const s of this.#sources) {
      if (s.wants && !s.wants(q)) {
        this.#rows.delete(s.id);
        this.#asking.delete(s.id);
        this.#failed.delete(s.id);
        this.#failedWhat.delete(s.id);
        continue;
      }
      const run = () => {
        let out: readonly Row[] | Promise<readonly Row[]>;
        try {
          out = s.rows(q);
        } catch (e) {
          return e instanceof PartialAnswer ? settle(s, e.rows, e) : settle(s, null);
        }
        if (!isPromise(out)) return settle(s, out);
        this.#asking.add(s.id);
        out.then(
          (rows) => settle(s, rows),
          (e: unknown) => (e instanceof PartialAnswer && !abort.signal.aborted ? settle(s, e.rows, e) : settle(s, abort.signal.aborted ? (this.#rows.get(s.id) ?? []) : null)),
        );
      };
      if (s.debounceMs) {
        this.#asking.add(s.id);
        let ran = false;
        const once = () => {
          if (ran) return;
          ran = true;
          run();
        };
        this.#due.push(once);
        this.#timers.push(this.#schedule(once, s.debounceMs));
      } else run();
    }
    asking = false;
    this.#onChange();
  }

  /** Stop asking: the bar closed. */
  close(): void {
    this.#stop();
    this.#generation++;
    this.#asking.clear();
  }
}

/** Where the bar's keys are: a row (by id) and one of its actions, by place and by name. */
export interface Cursor {
  id: string;
  action: number;
  /** The action's name (a go's label, `pin`): kept when the row is replaced by one that stands for it. */
  label?: string;
}

const nameOf = (a: Row['actions'][number]) => (a.kind === 'go' ? a.label : 'pin');
const cursorAt = (row: Row, action: number): Cursor => ({ id: row.id, action, label: nameOf(row.actions[action]) });

export const flatRows = (sections: readonly Section[]): Row[] => sections.flatMap((s) => s.rows);

/**
 * The cursor on `rows`: on its row still, where that row is listed, or on
 * the row that now stands for it (a branch's pull request in the place of
 * the guess the text made), on the action of the same name when it has
 * one, else at the same place; with no row of its own, on the first row's
 * first action.
 */
export function settleCursor(rows: readonly Row[], cursor: Cursor | null): Cursor | null {
  if (!rows.length) return null;
  const row = cursor && (rows.find((r) => r.id === cursor.id) ?? rows.find((r) => r.aliases?.includes(cursor.id)));
  if (!row) return cursorAt(rows[0], 0);
  const named = cursor.label === undefined ? -1 : row.actions.findIndex((a) => nameOf(a) === cursor.label);
  return cursorAt(row, named >= 0 ? named : Math.min(cursor.action, row.actions.length - 1));
}

/**
 * The cursor moved: to the next or previous row's first action (wrapping),
 * or to the next or previous action, on to the next row's first or back to
 * the previous row's last.
 */
export function moveCursor(rows: readonly Row[], cursor: Cursor | null, how: 'next-row' | 'prev-row' | 'next-action' | 'prev-action'): Cursor | null {
  const at = settleCursor(rows, cursor);
  if (!at) return null;
  const i = rows.findIndex((r) => r.id === at.id);
  const wrap = (n: number) => rows[(n + rows.length) % rows.length];
  switch (how) {
    case 'next-row':
      return cursorAt(wrap(i + 1), 0);
    case 'prev-row':
      return cursorAt(wrap(i - 1), 0);
    case 'next-action':
      return at.action + 1 < rows[i].actions.length ? cursorAt(rows[i], at.action + 1) : cursorAt(wrap(i + 1), 0);
    case 'prev-action': {
      if (at.action > 0) return cursorAt(rows[i], at.action - 1);
      const prev = wrap(i - 1);
      return cursorAt(prev, prev.actions.length - 1);
    }
  }
}
